import React from 'react';
import { Box, Typography, Paper, Button, Stack, CircularProgress, useTheme } from '@mui/material';
import { MdVerified } from 'react-icons/md';
import GuardIcon from '@/assets/icons/GuardIcon';
import LimitedTimeIcon from '@/assets/icons/LimitedTimeIcon';

interface StepProps {
  onNext: () => void | Promise<void>;
  isSubmitting?: boolean;
  onboardingData?: any;
}

const OneTimePaymentStep: React.FC<StepProps> = ({ onNext, isSubmitting = false, onboardingData }) => {
  const theme = useTheme();
  const pricing = onboardingData;
  const [timeLeft, setTimeLeft] = React.useState<{ hours: string; minutes: string; seconds: string }>({
    hours: '00',
    minutes: '00',
    seconds: '00'
  });

  React.useEffect(() => {
    if (!pricing?.expires_at) return;

    const targetDate = new Date(pricing.expires_at).getTime();

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference <= 0) {
        clearInterval(interval);
        setTimeLeft({ hours: '00', minutes: '00', seconds: '00' });
        return;
      }

      const totalHours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({
        hours: totalHours.toString().padStart(2, '0'),
        minutes: minutes.toString().padStart(2, '0'),
        seconds: seconds.toString().padStart(2, '0')
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [pricing?.expires_at]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* 🔥 PROMO BANNER */}

      {pricing?.discount_applied ? (
        <Paper
          elevation={0}
          sx={{
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderRadius: 3,
            bgcolor: '#F6E9D9',
            border: '1px solid #E5D3BC'
          }}
        >
          {/* LEFT ICON */}
          <Box
            sx={{
              width: 54,
              height: 54,
              bgcolor: '#fff',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}
          >
            <LimitedTimeIcon />
          </Box>

          {/* TEXT */}
          <Box sx={{ flex: 1 }}>
            <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap'>
              <Typography fontWeight={600} fontSize={{ xs: 12, md: 14 }} color='#D97706'>
                Limited-Time Offer - Expires Soon!
              </Typography>

              {/* TIMER */}
              <Stack direction='row' sx={{ alignItems: 'center' }} spacing={0.5}>
                {[timeLeft.hours, timeLeft.minutes, timeLeft.seconds].map((val, i) => (
                  <>
                    <Box
                      key={i}
                      sx={{
                        minWidth: 36,
                        height: 36,
                        bgcolor: '#fff',
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: { xs: 12, md: 14 },
                        color: '#DC2626'
                      }}
                    >
                      {val}
                    </Box>
                    <Typography key={`sep-${i}`} fontWeight={600} fontSize={{ xs: 12, md: 14 }} color='#D97706'>
                      {i < 2 && ':'}
                    </Typography>
                  </>
                ))}
              </Stack>
            </Box>

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 0.5,
                mt: 0.5
              }}
            >
              <Typography fontSize={{ xs: 10, md: 12 }} color='#707279'>
                Coupon Code
              </Typography>

              <Box
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  bgcolor: `${theme.palette.primary.main}0D`, // 0D is ~5% alpha
                  px: 0.7,
                  py: 0.5,
                  borderRadius: 1
                }}
              >
                <Typography fontSize={{ xs: 10, md: 12 }} color={theme.palette.primary.main} lineHeight='16px'>
                  {pricing?.onboarding_coupon_code || '---'}
                </Typography>
              </Box>

              <Typography fontSize={{ xs: 10, md: 12 }} color='#707279'>
                will be
              </Typography>

              <Typography fontSize={{ xs: 10, md: 12 }} color='#707279'>
                applied automatically
              </Typography>

              <Typography fontSize={{ xs: 10, md: 12 }} color='#707279'>
                at checkout
              </Typography>
            </Box>
          </Box>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderRadius: 3,
            bgcolor: '#F6E9D9',
            border: '1px solid #E5D3BC'
          }}
        >
          {/* LEFT ICON */}
          <Box
            sx={{
              width: 54,
              height: 54,
              bgcolor: '#fff',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5
            }}
          >
            <LimitedTimeIcon />
          </Box>

          {/* TEXT */}
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight={600} fontSize={14} color='#D97706'>
              Offer Expired
            </Typography>

            <Box display='flex' alignItems='center' gap={1}>
              <Typography display='flex' alignItems='center' gap={0.5} fontSize={12} color='text.secondary'>
                You missed the special offer. Regular setup pricing applies.
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* MAIN CARD */}
      <Paper sx={{ p: 3, borderRadius: 3, width: '100%' }}>
        <Typography fontWeight='bold' sx={{ fontSize: { xs: '16px', md: '24px' } }}>
          One-Time AI Agent Setup Fee
        </Typography>

        <Typography fontSize='14px' color='text.secondary' mt={1} mb={2}>
          A single payment to build, train & deploy your custom AI receptionist — no recurring setup charges.
        </Typography>

        {pricing?.discount_applied ? (
          <Stack direction='row' alignItems='center' spacing={2} mb={2}>
            <Typography sx={{ fontSize: { xs: 24, md: 40 } }} fontWeight='bold'>
              ${pricing?.final_price || '---'}
            </Typography>

            {pricing?.discount_applied && (
              <Typography
                sx={{
                  textDecoration: 'line-through',
                  color: '#9ca3af',
                  fontSize: { xs: 20, md: 24 }
                }}
              >
                ${pricing?.base_price || '---'}
              </Typography>
            )}

            {(pricing?.percent_off || pricing?.amount_off) && (
              <Box
                sx={{
                  background: '#dcfce7',
                  color: '#16a34a',
                  px: 2,
                  py: 0.5,
                  borderRadius: 20,
                  fontSize: 10,
                  fontWeight: 600
                }}
              >
                SAVE {pricing?.percent_off ? `${pricing.percent_off}%` : `$${pricing?.amount_off}`}
              </Box>
            )}
          </Stack>
        ) : (
          <Typography fontSize='40px' fontWeight='bold' mb={2.5}>
            ${pricing?.base_price || '---'}
          </Typography>
        )}

        {/* FEATURES */}
        <Stack mb={3}>
          {[
            'Custom AI agent trained on your business',
            'Seamless call handling & routing setup',
            'Knowledge base configuration',
            'Dedicated onboarding specialist'
          ].map((item, i) => (
            <Stack key={i} direction='row' mt={1} alignItems='center'>
              <GuardIcon />
              <Typography fontSize='14px'>{item}</Typography>
            </Stack>
          ))}
        </Stack>

        {/* BUTTON */}
        <Button
          fullWidth
          variant='contained'
          disabled={isSubmitting}
          onClick={() => void onNext()}
          sx={{
            py: 1.5,
            borderRadius: '30px',
            fontSize: 16,
            textTransform: 'none',
            background: theme.palette.primary.main,
            boxShadow: `0 6px 16px ${theme.palette.primary.main}40`,
            '&:hover': {
              background: theme.palette.primary.dark
            }
          }}
          endIcon={!isSubmitting ? <MdVerified /> : undefined}
        >
          {isSubmitting ? <CircularProgress size={20} color='inherit' /> : 'Pay Now'}
        </Button>

        {/* FOOTER */}
        <Stack direction='row' justifyContent='center' alignItems='center' mt={2} spacing={1}>
          <GuardIcon />
          <Typography fontSize='10px' variant='caption' color='text.secondary'>
            Secure checkout powered by Stripe. Apply coupon on the next screen
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default OneTimePaymentStep;
